import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2, Mail, Lock, Eye, EyeOff, ArrowRight, Phone, ShieldCheck } from "lucide-react";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [remember, setRemember] = useState(true);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const { login } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErr("");
    try {
      await login(email, password);
      navigate("/");
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen w-full bg-white relative overflow-hidden"
      style={{ fontFamily: "Manrope, sans-serif" }}
    >
      {/* Background kitchen image — full bleed */}
      <img
        src="/brand/kitchen-hero.jpg"
        alt=""
        aria-hidden="true"
        className="absolute inset-0 w-full h-full object-cover"
      />
      {/* Soft white wash so the left-side text stays legible */}
      <div className="absolute inset-0 bg-gradient-to-r from-white/85 via-white/55 to-white/0" />

      <div className="relative min-h-screen grid lg:grid-cols-[1.1fr_1fr] gap-0">
        {/* LEFT — Brand side */}
        <div className="flex flex-col justify-between p-8 sm:p-14 lg:p-16">
          {/* Logo + System driven */}
          <div className="flex items-start gap-4">
            <img
              src="/brand/hygiene-logo.png"
              alt="Hygiene Kitchens"
              className="h-20 sm:h-24 lg:h-28 w-auto"
              data-testid="login-logo"
            />
            <div className="hidden sm:block">
              <div
                className="text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[0.95] font-extrabold"
                style={{ fontFamily: "Bricolage Grotesque" }}
              >
                <span style={{ color: "#E52B50" }}>HYGIENE</span>
                <br />
                <span className="text-slate-900">KITCHENS</span>
              </div>
              <div className="mt-3 flex items-center gap-3">
                <span className="block h-[2px] w-10" style={{ background: "#E52B50" }} />
                <span className="text-[11px] font-bold tracking-[0.35em] text-slate-700">
                  SYSTEM DRIVEN
                </span>
              </div>
            </div>
          </div>

          {/* Big tagline */}
          <div className="hidden lg:block mt-10 mb-10">
            <h1
              className="text-7xl xl:text-8xl leading-[0.95] tracking-tighter font-extrabold"
              style={{ fontFamily: "Bricolage Grotesque" }}
            >
              <span style={{ color: "#E52B50" }}>From</span>
              <br />
              <span style={{ color: "#E52B50" }}>Dream</span>
              <br />
              <span className="text-slate-900">to</span>
              <br />
              <span className="text-slate-900">Execution</span>
            </h1>
            <div className="mt-6 h-[3px] w-16" style={{ background: "#E52B50" }} />
          </div>

          {/* Bottom — authorized + contact */}
          <div className="space-y-5">
            <div className="flex items-start gap-3 text-slate-700 max-w-md">
              <Lock className="w-4 h-4 mt-1 shrink-0" />
              <p className="text-sm leading-relaxed">
                This system is for authorized
                <br />
                Hygiene Kitchens Partners only.
              </p>
            </div>
            <div className="flex items-center gap-4 pt-2">
              <div
                className="w-12 h-12 rounded-full flex items-center justify-center shrink-0"
                style={{ background: "#F4F6F8", border: "1px solid #E52B50" }}
              >
                <Phone className="w-5 h-5" style={{ color: "#E52B50" }} />
              </div>
              <div>
                <div className="text-sm text-slate-600">Want to become our partner?</div>
                <div
                  className="text-xl font-extrabold"
                  style={{ fontFamily: "Bricolage Grotesque" }}
                >
                  <span className="text-slate-900">Call </span>
                  <a
                    href="tel:18005721691"
                    style={{ color: "#E52B50" }}
                    className="hover:underline"
                    data-testid="login-contact-number"
                  >
                    1800 572 1691
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT — Login card */}
        <div className="flex items-center justify-center p-6 sm:p-10 lg:p-12">
          <div
            className="w-full max-w-[460px] bg-white rounded-3xl shadow-2xl shadow-slate-900/10 px-8 sm:px-10 py-10 sm:py-12 border border-slate-100"
            data-testid="login-card"
          >
            {/* Card logo */}
            <div className="flex justify-center mb-7">
              <img
                src="/brand/hygiene-logo.png"
                alt="Hygiene Kitchens"
                className="h-14 sm:h-16 w-auto"
              />
            </div>

            <h2
              className="text-3xl sm:text-4xl font-extrabold text-center text-slate-900"
              style={{ fontFamily: "Bricolage Grotesque", letterSpacing: "-0.02em" }}
            >
              Welcome Back!
            </h2>
            <p className="text-center text-sm text-slate-500 mt-2 mb-8">
              Sign in to continue to Hygiene Kitchens System
            </p>

            <form onSubmit={submit} className="space-y-5" data-testid="login-form">
              {/* Email */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Email Address
                </label>
                <div className="relative">
                  <Mail className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="email"
                    required
                    autoComplete="username"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    className="w-full bg-white border border-slate-200 rounded-xl pl-11 pr-4 py-3.5 text-sm placeholder:text-slate-400 focus:outline-none focus:border-[#E52B50] focus:ring-2 focus:ring-[#E52B50]/20 transition-all"
                    data-testid="login-email-input"
                  />
                </div>
              </div>

              {/* Password */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Password
                </label>
                <div className="relative">
                  <Lock className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type={showPw ? "text" : "password"}
                    required
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    className="w-full bg-white border border-slate-200 rounded-xl pl-11 pr-12 py-3.5 text-sm placeholder:text-slate-400 focus:outline-none focus:border-[#E52B50] focus:ring-2 focus:ring-[#E52B50]/20 transition-all"
                    data-testid="login-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw((s) => !s)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 text-slate-400 hover:text-slate-700 transition-colors"
                    aria-label={showPw ? "Hide password" : "Show password"}
                    data-testid="login-password-toggle"
                  >
                    {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* Remember + Forgot */}
              <div className="flex items-center justify-between text-sm pt-1">
                <label className="inline-flex items-center gap-2.5 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={remember}
                    onChange={(e) => setRemember(e.target.checked)}
                    className="w-4 h-4 rounded accent-[#E52B50]"
                    data-testid="login-remember"
                  />
                  <span className="text-slate-700 font-medium">Remember Me</span>
                </label>
                <button
                  type="button"
                  className="font-bold hover:underline"
                  style={{ color: "#E52B50" }}
                  data-testid="login-forgot"
                  onClick={() => setErr("Please contact your Super Admin to reset your password.")}
                >
                  Forgot Password?
                </button>
              </div>

              {err && (
                <div
                  className="text-sm font-semibold rounded-lg px-3 py-2 bg-red-50 border border-red-200 text-red-700"
                  data-testid="login-error"
                >
                  {err}
                </div>
              )}

              {/* Submit */}
              <button
                type="submit"
                disabled={loading}
                className="w-full text-white font-bold rounded-xl py-4 mt-2 inline-flex items-center justify-center gap-2 disabled:opacity-60 transition-all hover:brightness-110 active:scale-[0.99] shadow-lg shadow-[#E52B50]/25"
                style={{ background: "#E52B50" }}
                data-testid="login-submit-button"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <span>Sign In</span>
                    <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </button>
            </form>

            {/* Trust strip */}
            <div className="mt-9 flex items-center gap-3">
              <span className="flex-1 h-px bg-slate-200" />
              <ShieldCheck className="w-4 h-4 text-slate-400" />
              <span className="flex-1 h-px bg-slate-200" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
